import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase.config';
import ImageLightbox from '../../components/ImageLightbox/ImageLightbox';
import '../../styles/kyro-design.css';

const DEFAULT_DEALS = [
  { type: 'PRE-IPO FUNDRAISE', typeClass: 'preipo' as const, company: 'The Hive Hostels (Colstay Limited)', sector: 'Luxury Student Housing & Co-Living', amount: '₹11.5 Cr', amountLabel: 'Pre-IPO Round Raised', desc: 'Acted as Corporate Advisor to Colstay Private Limited, operator of The Hive Hostels brand. Handholded the company through investor identification, pitch structuring and round closure. Marquee investors: Dr. Shriram Madhav Nene, Sahil Vachani (MD, Max Estates), Anchorage Capital Fund & Sixth Sense Ventures.', date: 'Aug 2024' },
  { type: 'PRE-IPO FUNDRAISE', typeClass: 'preipo' as const, company: 'Bhadora Industries Limited', sector: 'Electrical Cables & Infrastructure', amount: '₹5 Cr', amountLabel: 'Pre-IPO Round Raised', desc: 'Served as Corporate Advisor to Bhadora Industries Limited, guiding the company through its pre-IPO capital raise. Responsible for investor identification, transaction structuring and selection of intermediaries as part of the broader IPO readiness mandate.', date: 'Dec 2024' },
  { type: 'IPO', typeClass: 'ipo' as const, company: 'Bhadora Industries Limited', sector: 'Electrical Cables & Infrastructure', amount: '₹56 Cr', amountLabel: 'IPO Issue Size', desc: 'Following the pre-IPO round, continued as Corporate Advisor through the full IPO journey – coordinating selection of merchant banker, registrar and legal counsel, supporting investor roadshows and handholding management through SEBI filing to listing day. Kyro team has presented speech at NSE listing ceremony.', date: 'Aug 2025' },
  { type: 'IPO', typeClass: 'ipo' as const, company: 'EPW India Limited', sector: 'IT Electronics Refurbishing', amount: '₹32 Cr', amountLabel: 'IPO Issue Size', desc: 'Served as Corporate Advisor through the entire IPO lifecycle – from pre-IPO readiness to merchant banker selection, investor roadshows and listing. Listed on NSE, opened at 111-14.4% premium. Revenue +188% & PAT +485% YoY. Kyro team has presented speech at NSE listing ceremony.', date: 'Dec 2025' },
  { type: 'DEBT SYNDICATION', typeClass: 'debt' as const, company: 'Keemaya Resorts & Spas LLP', sector: 'Hospitality & Leisure', amount: '₹50 Cr', amountLabel: 'Issue Size', desc: 'Acted as Corporate Advisor for a ₹50 Crore debt round for Keemaya Resorts & Spas LLP. Responsible for lender identification, term sheet negotiation, intermediary selection and end-to-end handholding of the hospitality group through successful deal closure.', date: 'Jan 2025' },
];

const DEFAULT_GALLERIES = [
  { slug: 'epw', title: 'EPW India Limited', caption: 'EPW India Limited · NSE Listing — Kyro Capital served as Corporate Advisor through the full IPO lifecycle. Listed on NSE.', imageURLs: [2, 3, 4, 5, 6, 7, 8, 9].map((n) => `/assets/deals/epw-listing-${n}.jpeg`) },
  { slug: 'bhadora', title: 'Bhadora Industries Limited', caption: 'Bhadora Industries Limited · NSE Listing — Kyro Capital served as Corporate Advisor through the full IPO journey. Listed on NSE.', imageURLs: [1, 2, 3, 4, 5].map((n) => `/assets/deals/bhadora-listing/bhadora-listing-${n}.jpeg`) },
];

const advisoryServices = [
  { icon: '📈', title: 'Public Market & Listing Advisory', desc: 'We advise companies preparing to access public markets — strategic positioning, capital structuring, valuation assessment and investor readiness. Focus on institutional preparedness and long-term market credibility.', pts: [] },
  { icon: '🏛', title: 'Private Capital & Pre-Listing Advisory', desc: 'Support growth-oriented enterprises in raising strategic private capital ahead of listing or expansion. Advisory spans capital structuring, investor engagement, financial modelling and transaction advisory.', pts: [] },
  { icon: '📣', title: 'Investor Relations & Strategic Communications', desc: 'We advise listed companies on investor engagement and market communication strategy.', pts: ['Ongoing IR advisory', 'Quarterly earnings call coordination', 'Investor call preparation and management', 'Structured public relations and disclosure strategy'] },
  { icon: '🏦', title: 'Debt & Structured Capital Advisory', desc: 'Structured financing solutions aligned with long-term business objectives. Working with banks, NBFCs and institutional lenders on capital alternatives and optimising financing structures.', pts: [] },
  { icon: '👨‍👩‍👧', title: 'Family Office Advisory', desc: 'Strategic capital allocation, private market opportunities and long-term wealth structuring for family offices. Balancing growth, risk oversight and generational capital stewardship.', pts: [] },
];

interface DealData {
  type: string;
  typeClass: 'preipo' | 'ipo' | 'debt';
  company: string;
  sector: string;
  amount: string;
  amountLabel: string;
  desc: string;
  date: string;
}

interface GalleryData {
  slug: string;
  title: string;
  caption: string;
  imageURLs: string[];
}

const Advisory: React.FC = () => {
  const [deals, setDeals] = useState<DealData[]>(DEFAULT_DEALS);
  const [galleries, setGalleries] = useState<GalleryData[]>(DEFAULT_GALLERIES);
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number; title: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const dealsSnap = await getDocs(query(collection(db, 'deals'), orderBy('order')));
        if (!dealsSnap.empty) {
          setDeals(dealsSnap.docs.map((d) => ({ ...d.data(), typeClass: (d.data().typeClass || 'ipo') as DealData['typeClass'] } as DealData)));
        }
        const galleriesSnap = await getDocs(query(collection(db, 'dealGalleries'), orderBy('order')));
        if (!galleriesSnap.empty) {
          setGalleries(galleriesSnap.docs.map((d) => ({ ...d.data(), imageURLs: d.data().imageURLs || [] } as GalleryData)));
        }
      } catch {
        // use defaults
      }
    };
    load();
  }, []);

  return (
    <div className="page">
      <div className="hero" style={{ minHeight: '46vh', paddingBottom: '4rem' }}>
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="kicker">
            <div className="kicker-dot" />
            <div className="kicker-line" />
            Corporate Advisory
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>
            Capital Strategy.<br />
            <span className="dim">Institutional Execution.</span>
          </h1>
          <p className="hero-sub">
            Kyro Capital is the corporate advisory arm of the Kyro group, advising companies on capital formation, public market readiness and structured financing.
          </p>
        </div>
      </div>

      <div className="sec">
        <div className="eyebrow">Our Services</div>
        <div className="sec-title">Strategic Advisory Across the Capital Lifecycle.</div>
        <div className="adv-grid">
          {advisoryServices.map((s) => (
            <div className="adv-card" key={s.title}>
              <div className="adv-head">
                <div className="adv-icon">{s.icon}</div>
                <div className="adv-title">{s.title}</div>
              </div>
              <div className="adv-body">
                <div className="adv-desc">{s.desc}</div>
                {s.pts.length > 0 && (
                  <ul className="adv-pts">
                    {s.pts.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
          <div className="adv-card" style={{ border: '1px dashed var(--gray-lt)', background: 'var(--off)' }}>
            <div className="adv-head" style={{ background: 'var(--off)', borderBottom: '1px solid var(--gray-lt)' }}>
              <div className="adv-icon">+</div>
              <div className="adv-title" style={{ color: 'var(--gray)' }}>Custom Mandates</div>
            </div>
            <div className="adv-body">
              <div className="adv-desc" style={{ color: 'var(--gray)' }}>
                We work with a wide range of companies across capital market requirements. Reach out to explore how Kyro Capital can support your specific mandate.
              </div>
              <br />
              <Link to="/contact" style={{ fontSize: '0.8rem', color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>
                Start a conversation →
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="sec-off">
        <div className="eyebrow">Track Record</div>
        <div className="sec-title">Proven Deal Execution Experience (In last 18 Months)</div>
        <p className="sec-body" style={{ maxWidth: 620, marginBottom: '2rem' }}>
          A track record of successful transactions across pre-IPO fundraises, SME IPO listings and growth-stage capital raises.
        </p>
        <div className="track-grid">
          <div className="track-stat">
            <div className="track-v">₹550 Cr+</div>
            <div className="track-l">Total Value Advised</div>
          </div>
          <div className="track-stat">
            <div className="track-v">20+</div>
            <div className="track-l">Transactions Closed</div>
          </div>
          <div className="track-stat">
            <div className="track-v">18 Mo</div>
            <div className="track-l">Track Record Period</div>
          </div>
        </div>
        <p className="sec-body" style={{ maxWidth: 620, marginBottom: '1.5rem' }}>
          These are some of the transactions closed.
        </p>
        <div className="deal-cards">
          {deals.map((d) => (
            <div className="deal-card" key={`${d.company}-${d.type}-${d.date}`}>
              <div className={`deal-type ${d.typeClass}`}>{d.type}</div>
              <div className="deal-body">
                <div className="deal-company">{d.company}</div>
                <div className="deal-sector">{d.sector}</div>
                <div className="deal-amount">{d.amount} <span className="deal-amount-label">{d.amountLabel}</span></div>
                <p className="deal-desc">{d.desc}</p>
                <div className="deal-date">Corporate Advisor · {d.date}</div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: '0.68rem', color: 'var(--gray)', fontStyle: 'italic', marginTop: '1.5rem', textAlign: 'center' }}>
          Kyro Capital has advised and closed transactions across pre-IPO fundraises, SME IPOs and structured debt – with live media coverage and NSE listing presence. Past transaction experience of Kyro Capital Private Limited. Not indicative of future results. Full deal details available on request.
        </p>

        <div className="deal-gallery" style={{ marginTop: '3rem' }}>
          <div className="eyebrow">Deal Highlights</div>
          <div className="sec-title">Some of the Glimpses</div>
          {galleries.map((g, idx) => (
            <React.Fragment key={g.slug}>
              {g.caption && (
                <p className="sec-body" style={{ maxWidth: 520, marginBottom: '1.5rem', marginTop: idx > 0 ? '2.5rem' : 0 }}>
                  {g.caption}
                </p>
              )}
              {g.imageURLs.length > 0 && (
                <div className="gallery-grid">
                  {g.imageURLs.map((url, i) => (
                    <button
                      key={`${g.slug}-${i}`}
                      type="button"
                      className="gallery-item"
                      aria-label={`Open ${g.title} photo ${i + 1} in gallery`}
                      onClick={() => setLightbox({ urls: g.imageURLs, index: i, title: g.title })}
                    >
                      <img src={url} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <ImageLightbox
        open={lightbox !== null}
        urls={lightbox?.urls ?? []}
        index={lightbox?.index ?? 0}
        getAlt={(i) => (lightbox ? `${lightbox.title} — ${i + 1}` : '')}
        onClose={() => setLightbox(null)}
        onIndexChange={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : null))}
      />
    </div>
  );
};

export default Advisory;
